import React, { useState } from 'react';
import SEO from '../components/SEO';

const contactJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  name: 'Contact Flyairlinebooking',
  url: 'https://flyairlinebooking.com/contact',
  description: 'Get in touch with Flyairlinebooking support 24/7 via email, phone or our contact form.',
};

const FAQS = [
  { q: 'How do I change or cancel my booking?', a: 'Go to "My Bookings", enter your email, and click "Cancel Booking" on the relevant booking.' },
  { q: 'Is my payment information secure?', a: 'Yes. We use 256-bit SSL encryption. We never store your full card details.' },
  { q: 'How soon do I receive my e-ticket?', a: 'Your booking confirmation and PNR are sent immediately after payment.' },
  { q: 'Can I book for multiple passengers?', a: 'Yes — select up to 6 passengers during your search and fill in details for each.' },
];

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);

    if (typeof window.gtag === 'function') {
      window.gtag('event', 'conversion', { send_to: 'AW-17608432714/9_XtCPmW0dkcEMq4rcxB' });
    }
  };

  return (
    <div>
      <SEO
        title="Contact Us"
        canonical="/contact"
        description="Need help with your flight booking? Contact Flyairlinebooking support 24/7 via email at support@flyairlinebooking.com or call +1 (844) 480-0252."
        keywords="contact flyairlinebooking, flight booking support, customer service, help center, booking assistance"
        jsonLd={contactJsonLd}
      />
      {/* Hero */}
      <div className="bg-gradient-to-br from-emerald-900 to-green-900 text-white py-14 sm:py-20 px-4 text-center">
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold mb-4">Contact Us</h1>
        <p className="text-blue-100 text-lg max-w-xl mx-auto">
          Have a question or need help with a booking? Our team is here 24/7.
        </p>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-10 sm:py-16 grid md:grid-cols-2 gap-8 sm:gap-12">

        {/* Contact Info */}
        <div>
          <h2 className="text-2xl font-extrabold text-gray-900 mb-6">Get in Touch</h2>
          <div className="space-y-5">
            {[
              { icon: '📧', label: 'Email', value: 'support@flyairlinebooking.com' },
              { icon: '📞', label: 'Phone', value: '+1 (844) 480-0252' },
              { icon: '🕐', label: 'Hours', value: '24 / 7 — including holidays' },
              { icon: '📍', label: 'Address', value: '1309 Coffeen Ave STE 1200, Sheridan, WY 82801, USA' },
            ].map(item => (
              <div key={item.label} className="flex items-start gap-4 bg-blue-50 rounded-xl p-4">
                <span className="text-2xl">{item.icon}</span>
                <div>
                  <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide">{item.label}</p>
                  <p className="text-gray-800 font-medium mt-0.5">{item.value}</p>
                </div>
              </div>
            ))}
          </div>

          {/* FAQs */}
          <div className="mt-10">
            <h3 className="text-lg font-bold text-gray-800 mb-4">Frequently Asked Questions</h3>
            <div className="space-y-3">
              {FAQS.map(faq => (
                <details key={faq.q} className="bg-gray-50 rounded-xl border border-gray-100 group">
                  <summary className="px-4 py-3 text-sm font-semibold text-gray-800 cursor-pointer list-none flex justify-between items-center">
                    {faq.q}
                    <span className="text-gray-400 group-open:rotate-180 transition-transform">▼</span>
                  </summary>
                  <p className="px-4 pb-4 text-sm text-gray-600">{faq.a}</p>
                </details>
              ))}
            </div>
          </div>
        </div>

        {/* Contact Form */}
        <div>
          <h2 className="text-2xl font-extrabold text-gray-900 mb-6">Send a Message</h2>
          {submitted ? (
            <div className="bg-green-50 border border-green-200 rounded-2xl p-10 text-center">
              <div className="text-5xl mb-3">✅</div>
              <h3 className="font-bold text-green-700 text-xl mb-2">Message Sent!</h3>
              <p className="text-green-600 text-sm">We'll get back to you within 24 hours.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {[
                { name: 'name', label: 'Full Name', type: 'text', placeholder: 'John Doe' },
                { name: 'email', label: 'Email Address', type: 'email', placeholder: 'john@example.com' },
                { name: 'subject', label: 'Subject', type: 'text', placeholder: 'Booking issue, refund, etc.' },
              ].map(field => (
                <div key={field.name}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{field.label}</label>
                  <input
                    type={field.type}
                    required
                    placeholder={field.placeholder}
                    value={form[field.name]}
                    onChange={e => setForm({ ...form, [field.name]: e.target.value })}
                    className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              ))}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                <textarea
                  required
                  rows={5}
                  placeholder="Tell us how we can help..."
                  value={form.message}
                  onChange={e => setForm({ ...form, message: e.target.value })}
                  className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>
              <button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3.5 rounded-xl transition-colors"
              >
                Send Message →
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
